import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  InventoryAvailability,
  InventoryFamily,
  InventoryItem,
  InventoryLifecycle,
} from "../api/types";
import { Shell } from "../app/Shell";
import { ErrorNotice } from "../components/ui";
import { AdjustStockDialog } from "./AdjustStockDialog";
import { InventoryTable } from "./InventoryTable";
import { InventoryToolbar } from "./InventoryToolbar";
import { LowStockThresholdDialog } from "./LowStockThresholdDialog";
import { INVENTORY_PAGE_SIZE, useInventoryList } from "./useInventory";
import "./inventory.css";

const LIFECYCLES = new Set<InventoryLifecycle>([
  "operational",
  "unpublished",
  "archived",
]);

/**
 * Inventory: what the shop physically has, and every change to it.
 *
 * ONE system, not three. Glasses, contact lenses and accessories are filtered
 * within it rather than given screens of their own — an operator counting a
 * shelf does not first decide which kind of thing is on it, and three screens
 * would be three copies of the same table with three chances to drift.
 *
 * ONE list, not a list and a detail route. Movement history expands in place,
 * because checking why a count looks wrong is something done while scanning
 * the list and a round trip to another screen loses the operator's place in it.
 *
 * Every narrowing is applied by the server. The four states a real list has are
 * all real here — loading, empty, failed with a retry, populated — and a
 * failure is never allowed to render as an empty shop: those look identical to
 * an operator and mean opposite things.
 *
 * The filters live in the URL, unlike the Orders search box. Nothing here is
 * personal data: a product name, a shelf code and a colour are facts about the
 * shop, so a filtered view can be shared and reloaded without writing somebody
 * else's details into browser history.
 */
export function InventoryPage() {
  const [params, setParams] = useSearchParams();
  const [adjusting, setAdjusting] = useState<InventoryItem | null>(null);
  const [thresholdFor, setThresholdFor] = useState<InventoryItem | null>(null);
  const [openHistoryFor, setOpenHistoryFor] = useState<string | null>(null);

  const search = params.get("search") ?? "";
  const family = params.get("family") ?? "";
  const availability = params.get("availability") ?? "";
  const rawLifecycle = params.get("lifecycle") ?? "operational";
  const lifecycle: InventoryLifecycle = LIFECYCLES.has(
    rawLifecycle as InventoryLifecycle,
  )
    ? (rawLifecycle as InventoryLifecycle)
    : "operational";
  const page = Number(params.get("page") ?? "1") || 1;

  const query = {
    search: search || undefined,
    family: (family || undefined) as InventoryFamily | undefined,
    availability: (availability || undefined) as
      | InventoryAvailability
      | undefined,
    lifecycle,
    page,
    page_size: INVENTORY_PAGE_SIZE,
  };
  const inventory = useInventoryList(query);
  const data = inventory.data;

  /** Any change to what is being asked restarts paging: page three of the old
   *  result set is meaningless against a new one, and asking for it is how a
   *  narrowed search comes back empty when it had matches. */
  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  // The open drawer belongs to a row that may not be on the next page, and a
  // dialog belongs to a quantity that page is about to replace.
  function closeTransientState() {
    setOpenHistoryFor(null);
    setAdjusting(null);
    setThresholdFor(null);
  }

  const narrowed = Boolean(
    search || family || availability || lifecycle !== "operational",
  );

  return (
    <Shell title="Inventory">
      <div className="stack">
        <InventoryToolbar
          search={search}
          family={family}
          availability={availability}
          lifecycle={lifecycle}
          total={data ? data.total : null}
          onSearch={(value) => {
            closeTransientState();
            update({ search: value.trim() });
          }}
          onFamily={(value) => {
            closeTransientState();
            update({ family: value });
          }}
          onAvailability={(value) => {
            closeTransientState();
            update({ availability: value });
          }}
          onLifecycle={(value) => {
            closeTransientState();
            update({ lifecycle: value === "operational" ? "" : value });
          }}
          onClear={() => {
            closeTransientState();
            setParams(new URLSearchParams(), { replace: true });
          }}
        />

        {/* Retry sits beside the message rather than inside it, so the alert an
            assistive reader announces is the sentence and not the button. */}
        {inventory.isError ? (
          <div className="inventory__failure">
            <ErrorNotice error={inventory.error} />
            <button
              type="button"
              className="btn"
              disabled={inventory.isFetching}
              onClick={() => void inventory.refetch()}
            >
              {inventory.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        <div className="panel">
          {inventory.isPending ? (
            <div className="empty" role="status">
              Loading inventory…
            </div>
          ) : inventory.isError ? null : data && data.items.length === 0 ? (
            <div className="empty">
              {narrowed
                ? "No stock matches these filters."
                : "No stock yet. Items appear here once a product has been published."}
            </div>
          ) : data ? (
            <InventoryTable
              items={data.items}
              openHistoryFor={openHistoryFor}
              onToggleHistory={(itemId) =>
                setOpenHistoryFor((current) =>
                  current === itemId ? null : itemId,
                )
              }
              onAdjust={setAdjusting}
              onSetThreshold={setThresholdFor}
            />
          ) : null}
        </div>

        {data && data.total_pages > 1 ? (
          <div className="row" aria-label="Pagination">
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => {
                closeTransientState();
                update({ page: String(page - 1) });
              }}
            >
              Previous
            </button>
            <span className="muted num" role="status">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= data.total_pages}
              onClick={() => {
                closeTransientState();
                update({ page: String(page + 1) });
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {/* Keyed on the quantity the dialog was opened against, so a refetch that
          moves the item underneath it remounts the form rather than leaving a
          stale "current stock" beside a live preview. */}
      {adjusting ? (
        <AdjustStockDialog
          key={`${adjusting.inventory_item_id}-${adjusting.stock_quantity}`}
          item={
            data?.items.find(
              (row) => row.inventory_item_id === adjusting.inventory_item_id,
            ) ?? adjusting
          }
          onClose={() => setAdjusting(null)}
        />
      ) : null}

      {thresholdFor ? (
        <LowStockThresholdDialog
          key={`${thresholdFor.inventory_item_id}-${thresholdFor.version}`}
          item={
            data?.items.find(
              (row) => row.inventory_item_id === thresholdFor.inventory_item_id,
            ) ?? thresholdFor
          }
          onClose={() => setThresholdFor(null)}
        />
      ) : null}
    </Shell>
  );
}
